import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import c from 'classnames';

import {
  Button,
  ButtonColors,
  Card,
  ContainerSizes,
  PageContainer,
  Typography,
} from 'components';
import consults1on1Image from 'assets/images/consults_method_1on1.png';
import consultsGroupImage from 'assets/images/consults_method_group.png';
import { getConsultCreditsCount } from 'store/account';

import styles from './ConsultsSelectMethod.module.scss';

const consultOptions = [
  {
    id: 'group',
    imgUrl: consultsGroupImage,
    label: 'Wait: < 1 week',
    headline: 'Personalized Results Review',
    subheadline:
      'Small groups of ~4 people where you can get individual attention from a specialist',
    paymentInfo: 'FREE',
    tagLabel: 'Recommended',
  },
  {
    id: 'individual',
    imgUrl: consults1on1Image,
    label: 'Wait: 4+ weeks',
    headline: '1:1 call with a specialist',
    subheadline: '20 min appointment for complex topics and conditions',
    paymentInfo: '0 credits available, purchase needed',
    tagLabel: undefined,
  },
];

interface ConsultMethodCardProps {
  imgUrl: string;
  label: string;
  headline: string;
  subheadline: string;
  paymentInfo: string;
  tagLabel?: string;
  isSelected: boolean;
  onClick: () => void;
}

const ConsultMethodCard: React.FC<ConsultMethodCardProps> = ({
  imgUrl,
  //label,
  headline,
  subheadline,
  paymentInfo,
  tagLabel,
  isSelected,
  onClick,
}) => {
  // Temporarily commenting out label due to design changes (See #ENG-3663)
  return (
    <Card
      className={c(styles.card, { [styles.selected]: isSelected })}
      onClick={onClick}
      variant='wrapper'
    >
      <div className={styles.cardContainer}>
        <img src={imgUrl} alt={headline} className={styles.image} />
        <div className={styles.contentContainer}>
          {tagLabel && (
            <Typography variant='body-xs' className={styles.recommendedTag}>
              {tagLabel}
            </Typography>
          )}
          {/*<Typography variant='label' className={styles.label}>
            {label}
          </Typography>*/}
          <Typography variant='heading-m' className={styles.headline}>
            {headline}
          </Typography>
          <Typography variant='label' className={styles.subheadline}>
            {subheadline}
          </Typography>
          <Typography variant='label' className={styles.paymentInfo}>
            {paymentInfo}
          </Typography>
        </div>
      </div>
    </Card>
  );
};

const ConsultsSelectMethod: React.FC = () => {
  const consultCreditsCount = useSelector(getConsultCreditsCount);
  const [selectedMethod, setSelectedMethod] = useState<string | null>('group');
  const navigate = useNavigate();
  const { kitId } = useParams<{ kitId: string }>();

  const handleNext = () => {
    if (selectedMethod) {
      navigate(`/results/${kitId}/consults/book?method=${selectedMethod}`);
    }
  };

  return (
    <PageContainer size={ContainerSizes.LG} className={styles.container}>
      <Typography variant='heading-l' className={styles.title}>
        How would you like to review your results?
      </Typography>
      <div className={styles.cardsContainer}>
        {consultOptions.map(option => (
          <ConsultMethodCard
            key={option.id}
            imgUrl={option.imgUrl}
            label={option.label}
            headline={option.headline}
            subheadline={option.subheadline}
            tagLabel={option.tagLabel}
            isSelected={selectedMethod === option.id}
            onClick={() => setSelectedMethod(option.id)}
            paymentInfo={
              option.paymentInfo !== 'FREE' && !!consultCreditsCount
                ? `${consultCreditsCount} credit${consultCreditsCount === 1 ? '' : 's'} available`
                : option.paymentInfo
            }
          />
        ))}
      </div>
      <div className={styles.buttonContainer}>
        <Button
          onClick={handleNext}
          disabled={!selectedMethod}
          className={styles.nextButton}
          color={ButtonColors.PURPLE}
        >
          Next
        </Button>
      </div>
    </PageContainer>
  );
};

export default ConsultsSelectMethod;
